import { SceneCamera } from '../../core/Camera';
import type { SceneObject, TraceScene } from '../../exports/scene';
import { isEngineRoot, topLevelParent } from '../../exports/editor';

/** A build this many times the size of what was there, or this many metres across, is usually a unit slip. */
const RELATIVE_LIMIT = 10;
const ABSOLUTE_LIMIT_M = 20;

interface Box { min: [number, number, number]; max: [number, number, number] }

/** The world box of what renders under these objects, or null when nothing does. */
export function worldBounds(objects: readonly SceneObject[]): Box | null {
    let box: Box | null = null;
    for (const object of objects) {
        const b = SceneCamera.getNodeBounds(object.transform);
        if (!b) continue;
        const min = b.min.asArray(), max = b.max.asArray();
        box = box
            ? { min: box.min.map((v, i) => Math.min(v, min[i])) as Box['min'], max: box.max.map((v, i) => Math.max(v, max[i])) as Box['max'] }
            : { min, max };
    }
    return box;
}

/** The scene's content at the top of its document: what a build's size is measured against. */
export function topLevelObjects(traceScene: TraceScene): SceneObject[] {
    const root = topLevelParent(traceScene);
    return traceScene.getSceneObjects().filter(o => {
        if (o === root || isEngineRoot(o)) return false;
        const parent = o.getParent();
        return !parent || parent === root || isEngineRoot(parent);
    });
}

const extent = (box: Box): number => Math.max(...box.max.map((v, i) => v - box.min[i]));
const mm = (v: number) => Math.round(v * 1000) / 1000;

/**
 * The build's world box for its result, and a warning when it dwarfs the
 * content that was there before it, or is tens of metres across.
 */
export function boundsNote(built: Box | null, before: Box | null): string {
    if (!built) return '';
    const size = extent(built);
    let note = ` World bounds of this build: [${built.min.map(mm).join(', ')}] to [${built.max.map(mm).join(', ')}] m.`;
    const was = before ? extent(before) : 0;
    const over = [
        ...(was > 0 && size > RELATIVE_LIMIT * was ? [`${Math.round(size / was)}× the ${mm(was)} m of content already in the scene`] : []),
        ...(size > ABSOLUTE_LIMIT_M ? [`${Math.round(size)} m across`] : []),
    ];
    return over.length
        ? `${note} That is ${over.join(', and ')}: check for a size in cm or mm given as metres, or a scale binding that grows.`
        : note;
}
